import { clearAccountBrowser, requestDeletion, saveDeletion, type AccountDeletion } from "../lib/accountDeletion.js";
import { DeleteBlocked } from "../data/db.js";
import { el } from "../lib/dom.js";
import { messageOf, type Screen } from "./ui.js";

/** A stopped account stays outside the shell, including after an interrupted cleanup. */
export function accountDeletionScreen(state: AccountDeletion, stop: () => Promise<void>, sendNow = false): Screen {
  let busy = false;
  let disposed = false;
  const title = el("h1", { text: "Account deletion" });
  const status = el("p", { attrs: { role: "status" } });
  const retry = el("button", { text: "Retry deletion", attrs: { type: "button" }, on: { click: () => void run(true) } });
  const clear = el("button", { class: "secondary", text: "Clear this browser only", attrs: { type: "button" }, on: { click: () => {
    if (busy) return;
    state.phase = "local";
    try { saveDeletion(state); void run(false); } catch (error) { status.textContent = messageOf(error); }
  } } });
  const continueLink = el("a", { text: "Continue to sign in", attrs: { href: "/", hidden: true } });
  const node = el("section", { class: "screen centered account-deletion", children: [title, status,
    el("div", { class: "button-row", children: [retry, clear] }), continueLink] });
  function refresh() {
    retry.textContent = state.phase === "pending" ? "Retry deletion" : "Retry clearing this browser";
    clear.hidden = state.phase !== "pending";
    status.textContent = state.phase === "confirmed"
      ? "Account deleted. Clearing this browser…"
      : "Account deletion has not been confirmed. Retry, or clear this browser without confirming server deletion.";
  }
  async function run(send: boolean) {
    if (busy || disposed) return;
    busy = true; retry.disabled = true; clear.disabled = true;
    try {
      if (send && state.phase === "pending") await requestDeletion(state, stop);
      else await stop();
      if (state.phase === "pending") return;
      refresh();
      await clearAccountBrowser(state);
      if (disposed) return;
      title.textContent = state.phase === "confirmed" ? "Account deleted" : "Browser data cleared";
      status.textContent = state.phase === "confirmed"
        ? "Your account was deleted and its saved data was cleared from this browser."
        : "This browser's saved data was cleared. Server account deletion has not been confirmed.";
      retry.hidden = true; clear.hidden = true; continueLink.hidden = false; continueLink.focus();
    } catch (error) {
      if (!disposed) {
        refresh();
        // The one local failure a person can act on, so it is said as it is.
        const why = error instanceof DeleteBlocked ? error.message : messageOf(error);
        status.textContent = state.phase === "confirmed"
          ? `Account deleted, but this browser still needs clearing. ${why}`
          : state.phase === "local"
            ? `This browser still needs clearing. Reconnect and retry so Trayflux can confirm sign-out. ${why} Server account deletion has not been confirmed.`
            : `Deletion has not been confirmed. ${messageOf(error)} Retry or clear this browser only.`;
      }
    } finally { busy = false; retry.disabled = false; clear.disabled = false; }
  }
  // The live region must be mounted before its first message changes.
  queueMicrotask(() => {
    if (disposed) return;
    refresh();
    if (sendNow || state.phase !== "pending") void run(sendNow);
  });
  return { node, dispose() { disposed = true; }, busy: () => busy };
}
